import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { platformAPI, subscriptionAPI } from '../../services/api';
import { LoadingSpinner } from '../../components/ui';
import { EmptyState } from '../../components/ui/AdminPrimitives';
import {
  PlanReferenceCards,
  OrganizationPlanModal,
  OrganizationUsageBar,
  PlanTierBadge,
  SlugBadge,
  TenantStatusBadge,
} from '../../components/platform';
import type {
  PlatformOrganization,
  PublicPlanDefinition,
  SubscriptionTier,
} from '../../types/platformOrganization';

type TierFilter = 'all' | SubscriptionTier;

const PlatformPlans: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<PlatformOrganization | null>(null);

  const { data: plansRes } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );
  const publicPlans = (plansRes?.data?.data || []) as PublicPlanDefinition[];

  const { data: orgsRes, isLoading } = useQuery(['platform-organizations'], () =>
    platformAPI.listOrganizations()
  );
  const organizations = (orgsRes?.data?.data || []) as PlatformOrganization[];

  const filtered = useMemo(() => {
    let list = [...organizations];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.planLabel.toLowerCase().includes(q) ||
          o.tenants.some((t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q))
      );
    }
    if (tierFilter !== 'all') {
      list = list.filter((o) => o.tier === tierFilter);
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }, [organizations, search, tierFilter]);

  const summary = useMemo(() => {
    const byTier = { BASIC: 0, PROFESSIONAL: 0, PREMIUM: 0 };
    let mrr = 0;
    for (const o of organizations) {
      byTier[o.tier] = (byTier[o.tier] || 0) + 1;
      mrr += o.priceUsd;
    }
    return { total: organizations.length, byTier, mrr };
  }, [organizations]);

  const createOrg = useMutation(
    (body: { name: string; tier: SubscriptionTier }) => platformAPI.createOrganization(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-organizations']);
        toast.success('Cuenta creada');
        setCreateOpen(false);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } } };
        toast.error(e?.response?.data?.error || 'No se pudo crear la cuenta');
      },
    }
  );

  const updateOrg = useMutation(
    ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; tier?: SubscriptionTier };
    }) => platformAPI.updateOrganization(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-tenants']);
        toast.success('Plan actualizado');
        setEditOrg(null);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } } };
        toast.error(e?.response?.data?.error || 'No se pudo actualizar');
      },
    }
  );

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm ring-1 ring-slate-900/5 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Facturación SaaS
            </p>
            <h2 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              <SparklesIcon className="h-7 w-7 text-amber-600" aria-hidden />
              Planes y cuentas
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Administra el plan de cada cliente (organización), revisa el uso frente a los límites y
              asigna funerarias desde el panel de funerarias.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
          >
            <PlusIcon className="h-5 w-5" />
            Nueva cuenta
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <p className="text-xs text-slate-500">Cuentas activas</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{summary.total}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <p className="text-xs text-slate-500">MRR estimado</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">${summary.mrr}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <p className="text-xs text-slate-500">Plan Básico</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{summary.byTier.BASIC}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <p className="text-xs text-slate-500">Pro + Premium</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">
              {(summary.byTier.PROFESSIONAL || 0) + (summary.byTier.PREMIUM || 0)}
            </p>
          </div>
        </div>
      </section>

      {publicPlans.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Catálogo de planes</h3>
          <PlanReferenceCards plans={publicPlans} />
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/5">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Buscar cuenta o funeraria…"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as TierFilter)}
          >
            <option value="all">Todos los planes</option>
            <option value="BASIC">Básico ($20)</option>
            <option value="PROFESSIONAL">Profesional ($50)</option>
            <option value="PREMIUM">Premium ($100)</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Sin cuentas"
            description={
              organizations.length === 0
                ? 'Crea la primera cuenta de facturación o registra una funeraria.'
                : 'Ningún resultado coincide con el filtro.'
            }
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((org) => {
              const open = expandedId === org.id;
              const tenantsAtLimit =
                org.limits.maxTenants != null && org.usage.tenants >= org.limits.maxTenants;
              return (
                <li key={org.id}>
                  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={() => setExpandedId(open ? null : org.id)}
                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    >
                      {open ? (
                        <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-400 mt-0.5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">{org.name}</span>
                          <PlanTierBadge tier={org.tier} label={org.planLabel} />
                          <span className="text-sm font-medium text-slate-600 tabular-nums">
                            ${org.priceUsd}/mes
                          </span>
                          {tenantsAtLimit && (
                            <span className="rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-700 ring-1 ring-rose-200">
                              Límite funerarias
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {org.tenantCount} funeraria{org.tenantCount !== 1 ? 's' : ''} ·{' '}
                          {org.usage.users} usuarios · {org.usage.activeClients} clientes
                        </p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditOrg(org)}
                      className="shrink-0 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Editar plan
                    </button>
                  </div>

                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50/50 px-4 pb-4 pt-2 sm:pl-12">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="space-y-2.5 rounded-xl border border-slate-200 bg-white p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Uso vs límites
                          </p>
                          <OrganizationUsageBar
                            label="Funerarias"
                            used={org.usage.tenants}
                            max={org.limits.maxTenants}
                          />
                          <OrganizationUsageBar
                            label="Usuarios"
                            used={org.usage.users}
                            max={org.limits.maxUsers}
                          />
                          <OrganizationUsageBar
                            label="Clientes"
                            used={org.usage.activeClients}
                            max={org.limits.maxActiveClients}
                          />
                          <OrganizationUsageBar
                            label="Servicios este mes"
                            used={org.usage.servicesThisMonth}
                            max={org.limits.maxServicesPerMonth}
                          />
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                            Funerarias en esta cuenta
                          </p>
                          {org.tenants.length === 0 ? (
                            <p className="text-sm text-slate-500">
                              Sin funerarias.{' '}
                              <Link to="/platform" className="text-slate-800 font-medium underline">
                                Crear funeraria
                              </Link>{' '}
                              y asignarla a esta cuenta.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {org.tenants.map((t) => (
                                <li
                                  key={t.id}
                                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2"
                                >
                                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                                    <span className="text-sm font-medium text-slate-800 truncate">
                                      {t.name}
                                    </span>
                                    <SlugBadge>{t.slug}</SlugBadge>
                                    <TenantStatusBadge isActive={t.isActive} />
                                  </div>
                                  <Link
                                    to={`/platform/tenants/${t.id}`}
                                    className="text-xs font-medium text-slate-600 hover:text-slate-900"
                                  >
                                    Ver perfil →
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <OrganizationPlanModal
        isOpen={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSubmit={(payload) => createOrg.mutate(payload)}
        isLoading={createOrg.isLoading}
      />

      <OrganizationPlanModal
        isOpen={!!editOrg}
        mode="edit"
        organization={editOrg}
        onClose={() => setEditOrg(null)}
        onSubmit={(payload) => {
          if (!editOrg) return;
          updateOrg.mutate({ id: editOrg.id, body: payload });
        }}
        isLoading={updateOrg.isLoading}
      />
    </div>
  );
};

export default PlatformPlans;
